import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";
import { formatDateForApi, withDefaultDates } from "@/utils";
import { useInsightView } from "@/hooks/useInsightView";
import { useSearchSectionRoutes } from "@/hooks/useSearchSectionRoutes";
import { useFilterParamsV4 } from "@/hooks/useFilterParams";
import { usePageParam } from "@/hooks/usePageParam";
import {
  omitSheetParams,
  promoteSheetParams,
  useSheetFilterParams,
} from "@/hooks/useSheetFilterParams";
import { useLightSearch } from "@/hooks/useLightSearch";
import { useI18n } from "@/contexts/i18nContext";
import { differenceInDays, isSameHour, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import {
  getSearchFilterOptions,
  getSearchMentionsV4,
} from "@/api/endpoints/searches";
import SearchFilterBar from "@components/SearchFilterBar";
import { useFilterOrder } from "@components/SearchFilterBar/filterOrder";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@components/ui/sheet";
import {
  formatDateFromMCPMonitorTimeSeriesGranularity,
  getMCPMonitorTimeSeriesGranularityFromDayDiff,
} from "@components/widgets/utils";
import { Button } from "@/components/ui/button";
import { plural } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useEffect, useRef, useState } from "react";
import MentionCards from "@components/MentionCards";
import { PictureInPicture2Icon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import { postTrackingData } from "@/api/endpoints/track";
import { EVENT } from "@/tracking_events";

export function MentionsSheetView() {
  const { formatDateRange, formatDate } = useI18n();

  // URL state
  const insight = useInsightView();
  const section = useSearchSectionRoutes();

  const {
    uuid: searchUuid,
    orgUuid,
    startDate,
    endDate,
  } = useParams<{
    startDate: string;
    endDate: string;
    orgUuid: Uuid;
    uuid: Uuid;
  }>();

  const [pageFilters] = useFilterParamsV4();
  const [sheetFilters, setSheetFilters] = useSheetFilterParams();
  const [page, setPage] = usePageParam("sheet_page");
  const history = useHistory();
  const location = useLocation();

  // Server state
  const search = useLightSearch(searchUuid, orgUuid);

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);

  // Local state
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [filterOrder] = useFilterOrder(
    sheetFilters,
    setSheetFilters,
    search?.start_date,
    { insight, surface: "sheet" }
  );

  // Server state
  const {
    data: mentionCardsData,
    error: mentionCardsError,
    isLoading: isMentionCardsLoading,
  } = useQuery({
    queryKey: [searchUuid, "mentions", startDate, endDate, sheetFilters, page],
    queryFn: () =>
      getSearchMentionsV4(searchUuid, {
        ...sheetFilters,
        start_date: startDate,
        end_date: endDate,
        full_count: true,
        page,
      }),
  });

  const { data: searchFilterOptionsData } = useQuery({
    queryKey: ["searches", searchUuid, "filter-options"],
    queryFn: () => getSearchFilterOptions(searchUuid),
  });

  // Helpers
  const pageRange = withDefaultDates(pageFilters);
  const isPageRange =
    startDate === pageRange.start_date && endDate === pageRange.end_date;
  const count = mentionCardsData?.ok ? mentionCardsData.data.count : 0;

  const diffInDays = differenceInDays(pageRange.end_date, pageRange.start_date);
  const granularity = getMCPMonitorTimeSeriesGranularityFromDayDiff(diffInDays);

  const parsedStartDate = parseISO(startDate);
  const parsedEndDate = parseISO(endDate);

  const lede = formatDateRange(parsedStartDate, parsedEndDate, "PPP", {
    allowSameDay: isSameHour(parsedStartDate, parsedEndDate),
  });

  const closeTo = {
    overview: section.detail,
    reach: section.insightsReach,
    sentiment: section.insightsSentiment,
    results: section.insightsResults,
  }[insight];

  // Handlers
  function handleOpenChangeComplete(newIsOpen: boolean) {
    if (newIsOpen) {
      return;
    }

    history.replace({
      pathname: generatePath(closeTo, {
        uuid: searchUuid,
        orgUuid,
      }),
      search: isApplying
        ? promoteSheetParams(location.search, {
            start_date: formatDateForApi(parsedStartDate),
            end_date: formatDateForApi(parsedEndDate),
          })
        : omitSheetParams(location.search),
    });
  }

  function handleApply() {
    postTrackingData(EVENT.MENTIONS_SHEET_EXPANDED, { insight });
    setIsApplying(true);
    setIsOpen(false);
  }

  // Side-effects
  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsOpen(true));

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={setIsOpen}
      onOpenChangeComplete={handleOpenChangeComplete}
    >
      <SheetContent
        initialFocus={contentRef}
        ref={contentRef}
        className="gap-0"
      >
        <SheetHeader>
          <SheetTitle>
            {isPageRange
              ? lede
              : formatDateFromMCPMonitorTimeSeriesGranularity(
                  startDate,
                  granularity,
                  formatDate
                )}
          </SheetTitle>
          <div className="flex items-center gap-1">
            <SheetDescription>
              {count} {plural(count, { one: "inlägg", other: "inlägg" })}
              {lede && !isPageRange ? ` · ${lede}` : null}
            </SheetDescription>
          </div>
        </SheetHeader>

        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                className="absolute top-4 right-12"
                onClick={handleApply}
                variant="ghost"
                size="icon-sm"
              >
                <PictureInPicture2Icon />
              </Button>
            }
          />

          <TooltipContent>
            <Trans>Öppna i översikten</Trans>
          </TooltipContent>
        </Tooltip>

        {searchFilterOptionsData?.ok ? (
          <SearchFilterBar
            className="mx-6"
            filterOptions={searchFilterOptionsData.data}
            filterOrder={filterOrder}
            value={sheetFilters}
          />
        ) : null}

        <div className="overflow-y-auto px-6 pb-6 flex-1 pt-px mt-4">
          <MentionCards
            isMentionCardsLoading={isMentionCardsLoading}
            mentionCardsError={mentionCardsError}
            mentionCardsData={mentionCardsData}
            onChangePage={setPage}
            orgUuid={orgUuid}
            page={page}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
